import React, { useEffect, Fragment, useState } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";
import { DeleteItem, GetItems } from "../../../../api/medicalStore/master/itemAPI";


const ListItem = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ================= FETCH ITEMS =================
  const fetchData = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetItems();

      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,
          name: item.name || "",
          company: item.medicineCompanyId?.name || "",
          composition: item.medicineCompositionId?.name || "",
          group: item.medicineGroupId?.name || "",
          supplier: item.supplier || "",
          unit: item.unit || "",
          hsnCode: item.hsnCode || "",
          gst: item.gstPercentage || "",
          active: item.active || "No",
          createdAt: new Date(item.createdAt).toLocaleDateString()
        }));

        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message || "Something went wrong");
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ================= SEARCH =================
  useEffect(() => {
    const results = data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  // ================= ACTIONS =================
  const handleEditClick = id => {
    navigate(`/medical-store/master/item/edit/${id}`);
  };

  const handleDeleteClick = async id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This item will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeleteItem(id);
          if (response.success) {
            showToast("success", response.message);
            setData(data.filter(item => item.id !== id));
            setFilteredData(filteredData.filter(item => item.id !== id));
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  // ================= TABLE COLUMNS =================
  const columns = [
    { name: "S.No", selector: row => row.index, width:"10%" },
    { name: "Item Name", selector: row => row.name, sortable: true, width:"15%" },
    { name: "Company", selector: row => row.company, sortable: true, width:"15%" },
    { name: "Composition", selector: row => row.composition, width:"15%" },
    { name: "Group", selector: row => row.group, width:"15%" },
    { name: "Supplier", selector: row => row.supplier, width:"15%" },
    { name: "Unit", selector: row => row.unit, width:"10%" },
    { name: "HSN", selector: row => row.hsnCode, width:"10%" },
    { name: "GST %", selector: row => row.gst, width:"10%" },
    { name: "Active", selector: row => row.active, width:"10%" },
    {
      name: "Actions",
      cell: row => (
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil" />
          </button>
          <UncontrolledTooltip target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash" />
          </button>
          <UncontrolledTooltip target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>
      ),
      ignoreRowClick: true,
      button: true,
      width: "20%"
    }
  ];

  // ================= CSV EXPORT =================
  const headers = [
    { label: "Item Name", key: "name" },
    { label: "Company", key: "company" },
    { label: "Composition", key: "composition" },
    { label: "Group", key: "group" },
    { label: "Supplier", key: "supplier" },
    { label: "Unit", key: "unit" },
    { label: "HSN Code", key: "hsnCode" },
    { label: "GST %", key: "gst" },
    { label: "Active", key: "active" }
  ];

  const ExportCSV = ({ data }) => (
    <CSVLink data={data} headers={headers} filename="items.csv">
      <button className="btn btn-primary">Export</button>
    </CSVLink>
  );

  return (
    <Fragment>
      <Breadcrumb parent="Medical Store" title="List Items" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>Item List</h5>
            <Link to={`/medical-store/master/item/add`}>
              <button className="btn btn-primary">Create Item</button>
            </Link>
          </div>

          <div className="row p-3">
            <div className="col-md-6">
              <input
                className="form-control"
                placeholder="Search Item..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="col-md-6 text-end">
              <ExportCSV data={data} />
            </div>
          </div>

          <div className="card-body">
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
              striped
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListItem;
